import React from "react";
import { Dialog, DialogContent } from "@shared/ui/dialog";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

interface ViewContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: any;
}

export const ViewContractModal: React.FC<ViewContractModalProps> = ({
  isOpen,
  onClose,
  contract,
}) => {
  if (!contract) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white rounded-lg shadow-xl border p-0 overflow-hidden max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
          <PageHeader
            title="Contract Details"
            subtitle="View detailed information about the attached contract."
          />
        </div>

        {/* Body */}
        <div className="px-6 py-6 pb-8 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
            <div className="md:col-span-2">
              <ValueLabel label="Contract Name" value={contract.name} />
            </div>
            <ValueLabel 
              label="Category" 
              value={
                contract.consumerCategoriesDisplay?.filter((c: string) => c !== "").length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {contract.consumerCategoriesDisplay.filter((c: string) => c !== "").map((c: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md font-medium">
                        {c}
                      </span>
                    ))}
                  </div>
                ) : "N/A"
              } 
            />
            <ValueLabel 
              label="Sub Category" 
              value={
                contract.consumerSubCategoriesDisplay?.filter((c: string) => c !== "").length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {contract.consumerSubCategoriesDisplay.filter((c: string) => c !== "").map((c: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md font-medium">
                        {c}
                      </span>
                    ))}
                  </div>
                ) : "N/A"
              } 
            />
            <ValueLabel label="Attached On" value={contract.createdDate} />
            <ValueLabel label="Terms & Conditions" value={contract.termsAndConditions || "N/A"} />
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                Contract File
              </label>
              {contract.file ? (
                <a
                  href={contract.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-600 hover:underline flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open Document
                </a>
              ) : (
                <span className="text-sm font-medium text-gray-400 italic">No file attached</span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex justify-end gap-3 bg-gray-50 border-t border-gray-100 shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="px-5 h-9 text-sm text-gray-700 bg-white"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
